import { useCallback } from "react";

import {
    OrderedTransactions,
    simulateTransaction,
    isVersionedTransaction,
    BundleOptions,
    trySerializeAndEncodeTransaction
} from "@bridgesplit/react";
import {
    RPC_URL,
    simulateBundle,
    simulateTransactionAndGetBalanceChanges,
    resolveAddressLookups,
    sendTransactionAnalytics,
    AnalyticsInfo,
    BalanceChanges,
    TransactionStatus,
    IS_LOCAL_NX_DEV
} from "@bridgesplit/utils";
import { Connection, MessageAccountKeys, PublicKey, VersionedTransaction } from "@solana/web3.js";
import { useSelector } from "react-redux";

import { getSlice } from "../reducers/authSlice";
import { TransactionSimulationResult } from "../types";

// Shared connection instance — avoids re-instantiating on every simulation call
const connection = new Connection(RPC_URL);

/**
 * Simulates an OrderedTransactions sequence, returning the first error found.
 * Successful simulations return wallet balance deltas when captureBalanceChanges is enabled.
 * Non-throwing: captures unexpected errors as a simulation result.
 *
 * Strategy:
 * - Batches marked with BundleOptions.BundleOnly use simulateBundle (Jito), which is
 *   atomic and preserves state between transactions in the same batch.
 * - Other batches fall back to per-transaction simulateTransaction (RPC).
 */
export async function simulateOrderedTransactions(
    orderedTransactions: OrderedTransactions,
    userAddress: string,
    { captureBalanceChanges = false }: { captureBalanceChanges?: boolean } = {}
): Promise<TransactionSimulationResult | undefined> {
    try {
        const balanceChanges: BalanceChanges = {};

        for (const [batchIndex, { transactions, bundle }] of orderedTransactions.entries()) {
            if (bundle === BundleOptions.BundleOnly) {
                const hasLegacy = transactions.some(({ transaction }) => !isVersionedTransaction(transaction));
                if (hasLegacy) {
                    return { error: "BundleOnly batches must contain only versioned transactions." };
                }
                const encoded: string[] = [];
                for (const [transactionIndex, { transaction, identifier }] of transactions.entries()) {
                    const encodedTransaction = trySerializeAndEncodeTransaction(transaction);
                    if (encodedTransaction.isErr()) {
                        return {
                            error: `${formatSimulationTransactionLabel(batchIndex, transactionIndex, identifier)}: ${encodedTransaction.unwrapErrMessage()}`
                        };
                    }
                    encoded.push(encodedTransaction.unwrap());
                }
                if (!encoded.length) continue;

                const accountsConfig = captureBalanceChanges
                    ? {
                          accountsToTrack: await getAccountsToTrack(
                              transactions.map(({ transaction }) => transaction).filter(isVersionedTransaction),
                              userAddress
                          ),
                          userAddress
                      }
                    : undefined;
                const result = await simulateBundle(encoded, accountsConfig);
                if (IS_LOCAL_NX_DEV) {
                    // eslint-disable-next-line no-console
                    console.debug("[sim] simulateBundle returned", {
                        batchIndex,
                        ok: result.isOk(),
                        error: result.isErr() ? result.unwrapErrMessage() : undefined
                    });
                }
                if (result.isErr()) {
                    const individualResult = await simulateBatchTransactionsIndividually(
                        transactions,
                        userAddress,
                        batchIndex,
                        result.unwrapErrMessage()
                    );
                    if (individualResult) return individualResult;

                    return {
                        error: `Jito bundle simulation failed: ${result.unwrapErrMessage()}. Individual RPC simulations passed.`
                    };
                }

                if (captureBalanceChanges) {
                    mergeBalanceChanges(balanceChanges, result.unwrap().balanceChanges);
                }
            } else {
                if (IS_LOCAL_NX_DEV) {
                    // eslint-disable-next-line no-console
                    console.debug("[sim] using RPC simulateTransaction (per-tx)", {
                        batchIndex,
                        txCount: transactions.length
                    });
                }
                for (const { transaction } of transactions) {
                    if (
                        captureBalanceChanges &&
                        transactions.length === 1 &&
                        transaction instanceof VersionedTransaction
                    ) {
                        const accountsToTrack = (await getAccountsToTrack([transaction], userAddress)).map(
                            (address) => new PublicKey(address)
                        );
                        const result = await simulateTransactionAndGetBalanceChanges({
                            transaction,
                            user: new PublicKey(userAddress),
                            accountsToTrack
                        });

                        if (result.simulationLogs?.length) {
                            return {
                                error: result.simulationLogs[0],
                                logs: result.simulationLogs
                            };
                        }

                        mergeBalanceChanges(balanceChanges, result.balanceChanges);
                        continue;
                    }

                    const result = await simulateTransaction(connection, transaction, [userAddress]);

                    if (result.value?.err) {
                        return {
                            error: JSON.stringify(result.value.err),
                            logs: result.value.logs ?? undefined
                        };
                    }
                }
            }
        }

        return captureBalanceChanges ? { balanceChanges } : undefined;
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        return { error: message };
    }
}

async function getAccountsToTrack(transactions: VersionedTransaction[], userAddress: string): Promise<string[]> {
    const accounts = new Set<string>([userAddress]);

    for (const transaction of transactions) {
        const lutAccounts = await resolveAddressLookups(transaction);
        const accountKeys: MessageAccountKeys = transaction.message.getAccountKeys({
            addressLookupTableAccounts: lutAccounts
        });

        accountKeys.staticAccountKeys.forEach((account, index) => {
            if (transaction.message.isAccountWritable(index) || transaction.message.isAccountSigner(index)) {
                accounts.add(account.toBase58());
            }
        });

        accountKeys.accountKeysFromLookups?.writable.forEach((account) => accounts.add(account.toBase58()));
    }

    return Array.from(accounts);
}

function mergeBalanceChanges(target: BalanceChanges, source: BalanceChanges) {
    Object.entries(source).forEach(([mint, delta]) => {
        target[mint] = (target[mint] ?? 0) + delta;
    });
}

async function simulateBatchTransactionsIndividually(
    transactions: OrderedTransactions[number]["transactions"],
    userAddress: string,
    batchIndex: number,
    bundleError: string
): Promise<TransactionSimulationResult | undefined> {
    for (const [transactionIndex, { transaction, identifier }] of transactions.entries()) {
        const transactionLabel = formatSimulationTransactionLabel(batchIndex, transactionIndex, identifier);
        try {
            const result = await simulateTransaction(connection, transaction, [userAddress]);
            if (result.value?.err) {
                return {
                    error: `Jito bundle simulation failed: ${bundleError}. Individual simulation failed at ${transactionLabel}: ${JSON.stringify(result.value.err)}`,
                    logs: result.value.logs ?? undefined
                };
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            return {
                error: `Jito bundle simulation failed: ${bundleError}. Individual simulation errored at ${transactionLabel}: ${message}`
            };
        }
    }

    return undefined;
}

function formatSimulationTransactionLabel(batchIndex: number, transactionIndex: number, identifier?: string) {
    const label = `batch ${batchIndex + 1}, transaction ${transactionIndex + 1}`;
    return identifier ? `${label} (${identifier})` : label;
}

/**
 * Returns a factory that fires simulateOrderedTransactions non-blocking, capturing the current
 * wallet address from Redux at call time.
 *
 * Pass { skipSimulate: true } for transactions that should not simulate — the factory
 * resolves immediately to undefined without making any RPC call.
 * If no wallet is connected, also resolves to undefined immediately.
 * Pass captureBalanceChanges to request pre/post wallet accounts from bundle simulations.
 *
 * Pass analyticsInfo as a second argument to automatically POST to /analytics when simulation
 * fails. The POST fires as a side effect; the resolved value is unchanged.
 */
export function useCreateSimulationPromise({
    skipSimulate = false,
    captureBalanceChanges = false
}: { skipSimulate?: boolean; captureBalanceChanges?: boolean } = {}) {
    const walletAddress = useSelector((state: Parameters<typeof getSlice>[0]) => getSlice(state).stateWalletAddress);

    return useCallback(
        async (
            transactions: OrderedTransactions,
            analyticsInfo?: AnalyticsInfo
        ): Promise<TransactionSimulationResult | undefined> => {
            if (skipSimulate || !walletAddress) return undefined;
            const result = await simulateOrderedTransactions(transactions, walletAddress, { captureBalanceChanges });
            if (result?.error && analyticsInfo) {
                void sendTransactionAnalytics(walletAddress, {
                    ...analyticsInfo,
                    simulation: true,
                    transactionStatus: TransactionStatus.Error,
                    errorMessage: result.error
                });
            }
            return result;
        },
        [captureBalanceChanges, skipSimulate, walletAddress]
    );
}
